import { useEffect, useState } from "react";
import type { PromiseWithServerResult } from "@rocicorp/zero";
import type { Food } from "@corpuscomplete/core/nutrition/food";
import { PlannedMealDetail } from "@corpuscomplete/core-ui/nutrition/planned-meal/detail";
import { NutrientUI } from "@corpuscomplete/core-ui/nutrition/nutrient";
import type { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { FoodSearchUI } from "@corpuscomplete/core-ui/nutrition/food/search";
import { FoodItem, FoodItemInputView } from "@corpuscomplete/web/components/nutrition/food-item";
import { FoodSearchCombobox } from "@corpuscomplete/web/components/nutrition/food-search-combobox";
import { FoodSearchDialog } from "@corpuscomplete/web/components/nutrition/food-search-dialog";
import { VerticalDragReorderShellRow } from "@corpuscomplete/web/components/common/vertical-drag-reorder";
import { useVerticalDragReorder } from "@corpuscomplete/web/hooks/use-vertical-drag-reorder";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@corpuscomplete/web-ui/components/card";
import { Input } from "@corpuscomplete/web-ui/components/input";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@corpuscomplete/web-ui/components/item";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Tooltip, TooltipContent, TooltipTrigger } from "@corpuscomplete/web-ui/components/tooltip";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { CircleHelpIcon, XIcon } from "lucide-react";

export function PlannedMealEditorSections({
  canEditContent,
  canEditSchedule,
  currentUserId,
  defaultScheduleDate,
  defaultScheduleTimeZone,
  linkedAssignments = false,
  meal,
  memberLabels,
  memberUserIds,
  mutate,
  onRename,
  violations,
}: {
  canEditContent: boolean;
  canEditSchedule: boolean;
  currentUserId: string;
  defaultScheduleDate?: Temporal.PlainDate;
  defaultScheduleTimeZone?: string;
  linkedAssignments?: boolean;
  meal: ReturnType<typeof PlannedMealDetail.useMeal> & {};
  memberLabels?: ReadonlyMap<string, string>;
  memberUserIds?: readonly string[];
  mutate: (result: PromiseWithServerResult | undefined) => void;
  onRename: (name: string) => void;
  violations: PlannedMealDetail.PublishViolation[];
}) {
  return (
    <>
      <BasicsCard canEditContent={canEditContent} meal={meal} onRename={onRename} />
      <PublishIssuesCard violations={violations} linkedAssignments={linkedAssignments} />
      <PlatesSection
        canEditContent={canEditContent}
        currentUserId={currentUserId}
        meal={meal}
        memberLabels={memberLabels}
        memberUserIds={memberUserIds}
        mutate={mutate}
      />
      <EventsSection
        canEditSchedule={canEditSchedule}
        defaultScheduleDate={defaultScheduleDate}
        defaultScheduleTimeZone={defaultScheduleTimeZone}
        meal={meal}
        memberLabels={memberLabels}
        mutate={mutate}
      />
    </>
  );
}

function BasicsCard({
  canEditContent,
  meal,
  onRename,
}: {
  canEditContent: boolean;
  meal: ReturnType<typeof PlannedMealDetail.useMeal> & {};
  onRename: (name: string) => void;
}) {
  const [name, setName] = useState(meal.name);
  useEffect(() => setName(meal.name), [meal.id, meal.name]);
  useEffect(() => {
    if (!canEditContent || name === meal.name) return;
    const timeout = setTimeout(() => onRename(name), 500);
    return () => clearTimeout(timeout);
  }, [canEditContent, meal.name, name, onRename]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meal Basics</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          Name
          <Input
            disabled={!canEditContent}
            placeholder="Unnamed planned meal"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <div className="flex flex-col gap-1 text-sm">
          Type label
          <div className="flex h-9 items-center rounded-md border bg-muted px-3 text-sm text-muted-foreground">
            {meal.typeLabel}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PublishIssuesCard({
  linkedAssignments,
  violations,
}: {
  linkedAssignments: boolean;
  violations: PlannedMealDetail.PublishViolation[];
}) {
  if (violations.length === 0 && !linkedAssignments) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Publish Status / Validation</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-1">
        {violations.map((violation) => (
          <Text key={violation.key} variant="sm">
            {violation.message}
          </Text>
        ))}
        {linkedAssignments && (
          <Text variant="sm">
            Unpublish and assignment changes may be blocked by linked food logs.
          </Text>
        )}
      </CardContent>
    </Card>
  );
}

function PlatesSection({
  canEditContent,
  currentUserId,
  meal,
  memberLabels,
  memberUserIds,
  mutate,
}: {
  canEditContent: boolean;
  currentUserId: string;
  meal: ReturnType<typeof PlannedMealDetail.useMeal> & {};
  memberLabels?: ReadonlyMap<string, string>;
  memberUserIds?: readonly string[];
  mutate: (result: PromiseWithServerResult | undefined) => void;
}) {
  const current = PlannedMealDetail.currentUserPlate(meal, currentUserId);
  const plates = [
    ...(current ? [current] : []),
    ...PlannedMealDetail.otherPlates(meal, currentUserId),
  ];
  const mutations = PlannedMealDetail.useMutations();
  const assignedUserIds = new Set(meal.plates.map((plate) => plate.userId));
  const missingMemberUserIds = (memberUserIds ?? []).filter(
    (userId) => !assignedUserIds.has(userId),
  );
  return (
    <div className="flex flex-col gap-4">
      {missingMemberUserIds.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Add Plates</CardTitle>
            <CardDescription>Add session members to this planned meal explicitly.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {missingMemberUserIds.map((userId) => (
              <Button
                key={userId}
                variant="outline"
                size="sm"
                disabled={!canEditContent}
                onClick={() => mutate(mutations.addPlate({ userId }))}
              >
                Add plate for {getMemberLabel(userId, memberLabels)}
              </Button>
            ))}
          </CardContent>
        </Card>
      )}
      {plates.map((plate) => (
        <PlateCard
          key={plate.id}
          canEditContent={canEditContent}
          isCurrentUser={plate.userId === currentUserId}
          memberLabel={getMemberLabel(plate.userId, memberLabels)}
          plate={plate}
          mutate={mutate}
        />
      ))}
    </div>
  );
}

function PlateCard({ canEditContent, isCurrentUser, memberLabel, plate, mutate }: any) {
  const mutations = PlannedMealDetail.usePlateMutations(plate.id);
  const searchState = FoodSearchUI.useSearchState();
  const [foodSearchOpen, setFoodSearchOpen] = useState(false);
  const targets = plate.nutrientTargets;
  const entries = PlannedMealDetail.mergedPlateEntries(plate);
  const addSelectedFood = (selected: Food.Item) => {
    mutate(
      mutations.addItem({
        foodServingId: selected.serving.id,
        amount: 1,
      }),
    );
  };

  const closeFoodSearch = () => {
    setFoodSearchOpen(false);
    searchState.reset();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isCurrentUser ? "My Plate" : `${memberLabel}'s Plate`}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="grid gap-2 sm:grid-cols-4">
          {(["energy", "protein", "carbohydrate", "fat"] as const).map((key) => (
            <label key={key} className="flex flex-col gap-1 text-xs capitalize">
              {key} target
              <Input
                type="number"
                disabled={!canEditContent}
                value={targets?.[key]?.type === "exact" ? targets[key].value : ""}
                onChange={(event) => {
                  const merged = NutrientUI.mergeExactTargetInputs({
                    existingTargets: targets,
                    inputs: { [key]: event.target.value } as NutrientUI.TargetInputs,
                    keys: [key],
                  });
                  if (!merged.ok) {
                    return;
                  }
                  mutate(mutations.editTargets(merged.value as Nutrient.Targets | null));
                }}
              />
            </label>
          ))}
        </div>
        {canEditContent ? (
          <FoodSearchCombobox
            placeholder="Search to add a food"
            onSelect={addSelectedFood}
            onOpenAdvancedSearch={({ text }) => {
              searchState.onChangeText(text);
              setFoodSearchOpen(true);
            }}
          />
        ) : null}
        <FoodSearchDialog
          open={foodSearchOpen}
          onOpenChange={(open) => {
            if (open) {
              setFoodSearchOpen(true);
              return;
            }

            closeFoodSearch();
          }}
          searchState={searchState}
          onSelectItem={(selected) => {
            addSelectedFood(selected);
            searchState.onChangeText("");
          }}
          placeholder="Search to add a food"
        />
        <PlannedMealPlateEntriesList
          canEditContent={canEditContent}
          entries={entries}
          mutate={mutate}
          mutations={mutations}
        />
      </CardContent>
    </Card>
  );
}

function PlannedMealPlateEntriesList({
  canEditContent,
  entries,
  mutate,
  mutations,
}: {
  canEditContent: boolean;
  entries: ReturnType<typeof PlannedMealDetail.mergedPlateEntries>;
  mutate: (result: PromiseWithServerResult | undefined) => void;
  mutations: PlannedMealDetail.PlateMutations;
}) {
  const reorder = useVerticalDragReorder({
    items: entries,
    getItemId: (entry) => entry.id,
    getItemSignature: (entry) => {
      if (entry.type === "item") {
        return `${entry.type}:${entry.sortIndex}:${entry.item.amount}:${entry.item.serving.id}`;
      }

      return `${entry.type}:${entry.sortIndex}:${JSON.stringify(entry.descriptor.data)}`;
    },
    onReorder: ({ activeId, dropTarget }) => {
      mutate(
        mutations.reorderItem({
          activeId,
          dropTarget,
        }),
      );
    },
  });

  if (!canEditContent) {
    return (
      <div className="flex flex-col rounded-lg border">
        {entries.map((entry) => (
          <PlannedMealPlateEntryRow key={entry.id} entry={entry} readOnly />
        ))}
      </div>
    );
  }

  return (
    <DndContext {...reorder.dndContextProps}>
      <div className="flex flex-col rounded-lg border">
        {entries.map((entry) => (
          <VerticalDragReorderShellRow
            key={entry.id}
            className={cn("cursor-default", entry.type === "descriptor" && "bg-destructive/5")}
            {...reorder.getRowProps(entry.id)}
          >
            <PlannedMealPlateEntryRow
              entry={entry}
              onEditItemAmount={(amount) =>
                entry.type === "item" ? mutate(mutations.editItem(entry.item.id, { amount })) : null
              }
              onEditItemServing={(serving) =>
                entry.type === "item"
                  ? mutate(
                      mutations.editItem(entry.item.id, {
                        foodServingId: serving.id,
                        amount: serving.defaultAmount,
                      }),
                    )
                  : null
              }
              onRemoveItem={() =>
                entry.type === "item" ? mutate(mutations.removeItem(entry.item.id)) : null
              }
              onRemoveDescriptor={() =>
                entry.type === "descriptor"
                  ? mutate(mutations.removeDescriptor(entry.descriptor.id))
                  : null
              }
            />
          </VerticalDragReorderShellRow>
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {reorder.activeItem ? (
          <div
            className="rounded-lg bg-background/95 shadow-lg ring-1 ring-border/80"
            style={reorder.overlayWidth ? { width: reorder.overlayWidth } : undefined}
          >
            <PlannedMealPlateEntryRow entry={reorder.activeItem} readOnly />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function PlannedMealPlateEntryRow({
  entry,
  readOnly = false,
  onEditItemAmount,
  onEditItemServing,
  onRemoveItem,
  onRemoveDescriptor,
}: {
  entry: ReturnType<typeof PlannedMealDetail.mergedPlateEntries>[number];
  readOnly?: boolean;
  onEditItemAmount?: (amount: number) => void;
  onEditItemServing?: Parameters<typeof FoodItemInputView>[0]["onEditItemServing"];
  onRemoveItem?: () => void;
  onRemoveDescriptor?: () => void;
}) {
  if (entry.type === "descriptor") {
    return (
      <PlannedMealDescriptorRow
        descriptor={entry.descriptor.data}
        onRemove={readOnly ? undefined : onRemoveDescriptor}
      />
    );
  }

  if (readOnly) {
    return <FoodItem item={entry.item} titleLines={2} className="rounded-none py-2" />;
  }

  return (
    <FoodItemInputView
      className="flex-1 rounded-none py-2"
      itemInput={{ amount: entry.item.amount, foodServingId: entry.item.serving.id }}
      titleLines={2}
      onEditItemAmount={(amount) => onEditItemAmount?.(amount)}
      onEditItemServing={(serving) => onEditItemServing?.(serving)}
      onRemoveItem={() => onRemoveItem?.()}
    />
  );
}

function PlannedMealDescriptorRow({
  descriptor,
  onRemove,
}: {
  descriptor: {
    name: string;
    quantity: number | null;
    unit: string | null;
  };
  onRemove?: () => void;
}) {
  const quantityStr = descriptor.quantity !== null ? `${descriptor.quantity} ` : "";
  const unitStr = descriptor.unit !== null ? `${descriptor.unit}` : "";
  const amountLabel = `${quantityStr}${unitStr}`.trim();

  return (
    <Item
      variant="muted"
      size="xs"
      className="rounded-none border-transparent bg-transparent py-2 shadow-none"
    >
      <ItemContent>
        <div className="flex min-w-0 items-start gap-2.5">
          <ItemMedia
            variant="image"
            className="mt-0.5 size-7 rounded-none bg-muted text-muted-foreground [&_svg]:size-4"
          >
            <CircleHelpIcon className="size-4" />
          </ItemMedia>
          <div className="min-w-0 flex-1">
            <ItemTitle>{descriptor.name}</ItemTitle>
            <ItemDescription className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
              {amountLabel ? <span>{amountLabel}</span> : null}
              <Tooltip>
                <TooltipTrigger render={<span className="inline-flex" />}>
                  <Badge variant="destructive">Needs match</Badge>
                </TooltipTrigger>
                <TooltipContent>
                  This item still needs to be matched to a food before the planned meal can be
                  published.
                </TooltipContent>
              </Tooltip>
            </ItemDescription>
          </div>
        </div>
      </ItemContent>
      <ItemActions className="gap-1 self-start pt-0.5">
        {onRemove ? (
          <Button
            data-no-drag
            onPointerDown={(event) => event.stopPropagation()}
            onClick={onRemove}
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <XIcon className="size-4" />
          </Button>
        ) : null}
      </ItemActions>
    </Item>
  );
}

function EventsSection({
  canEditSchedule,
  defaultScheduleDate,
  defaultScheduleTimeZone,
  meal,
  memberLabels,
  mutate,
}: any) {
  const mutations = PlannedMealDetail.useMutations();
  const timeZone = defaultScheduleTimeZone ?? Temporal.Now.timeZoneId();
  const today = defaultScheduleDate ?? Temporal.Now.plainDateISO(timeZone);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>Events and Assignments</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canEditSchedule}
              onClick={() =>
                mutate(
                  mutations.addEvent({
                    timeAt: today
                      .toZonedDateTime({ timeZone, plainTime: Temporal.PlainTime.from("12:00") })
                      .toInstant(),
                    timeZone,
                  }),
                )
              }
            >
              Add Event
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {meal.events.length === 0 ? (
          <Text variant="sm" className="text-muted-foreground">
            No events yet. Drafts can stay unscheduled until you are ready to publish.
          </Text>
        ) : null}
        {meal.events.map((event: any) => (
          <EventRow
            key={event.id}
            canEditSchedule={canEditSchedule}
            defaultScheduleDate={defaultScheduleDate}
            defaultScheduleTimeZone={defaultScheduleTimeZone}
            event={event}
            memberLabels={memberLabels}
            meal={meal}
            mutate={mutate}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function EventRow({
  canEditSchedule,
  defaultScheduleDate,
  defaultScheduleTimeZone,
  event,
  memberLabels,
  meal,
  mutate,
}: any) {
  const mutations = PlannedMealDetail.useEventMutations(event.id);
  const zoned = event.timeAt.toZonedDateTimeISO(event.timeZone);
  const defaultScheduleDateText = (defaultScheduleDate ?? zoned.toPlainDate()).toString();
  const [date, setDate] = useState(zoned.toPlainDate().toString());
  const [time, setTime] = useState(zoned.toPlainTime().toString({ smallestUnit: "minute" }));
  useEffect(() => {
    setDate(defaultScheduleDateText);
  }, [defaultScheduleDateText]);
  const assignedPlateIds = new Set(event.assignments.map((assignment: any) => assignment.plateId));
  const unassignedPlates = meal.plates.filter((plate: any) => !assignedPlateIds.has(plate.id));
  return (
    <div className="flex flex-col gap-2 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <Badge variant="secondary">Event</Badge>{" "}
          <Text as="span" variant="sm">
            {PlannedMealDetail.eventSummary(event)}
          </Text>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={!canEditSchedule}
          onClick={() => mutate(mutations.removeEvent())}
        >
          Remove
        </Button>
      </div>
      <div className="flex gap-2">
        <Input
          type="date"
          disabled={!canEditSchedule}
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />
        <Input
          type="time"
          disabled={!canEditSchedule}
          value={time}
          onChange={(event) => setTime(event.target.value)}
        />
        <Button
          variant="outline"
          disabled={!canEditSchedule}
          onClick={() =>
            mutate(
              mutations.editEvent({
                timeAt: Temporal.PlainDate.from(date)
                  .toZonedDateTime({
                    timeZone: defaultScheduleTimeZone ?? event.timeZone,
                    plainTime: Temporal.PlainTime.from(time),
                  })
                  .toInstant(),
                timeZone: defaultScheduleTimeZone ?? event.timeZone,
              }),
            )
          }
        >
          Update
        </Button>
      </div>
      <div className="flex flex-col gap-1">
        {event.assignments.map((assignment: any) => {
          const plate = meal.plates.find((candidate: any) => candidate.id === assignment.plateId);
          return (
            <div key={assignment.id} className="flex items-center gap-2 text-sm">
              <Text className="flex-1">
                {getMemberLabel(assignment.userId, memberLabels)} assigned to{" "}
                {plate ? "their plate" : "missing plate"}
              </Text>
              <Button
                variant="ghost"
                size="sm"
                disabled={!canEditSchedule}
                onClick={() => mutate(mutations.removeAssignment(assignment.id))}
              >
                Remove
              </Button>
            </div>
          );
        })}
        <div className="flex flex-wrap gap-2">
          {unassignedPlates.map((plate: any) => (
            <Button
              key={plate.id}
              variant="outline"
              size="sm"
              disabled={!canEditSchedule}
              onClick={() => mutate(mutations.addAssignment(plate.id))}
            >
              Assign {getMemberLabel(plate.userId, memberLabels)}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

function getMemberLabel(userId: string, labels?: ReadonlyMap<string, string>): string {
  return labels?.get(userId) ?? (userId.length > 8 ? `${userId.slice(0, 8)}...` : userId);
}
